import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Phone, MessageCircle, CheckCircle2, AlertCircle, Plus, Shield, X, Loader2, ExternalLink, RefreshCw, PlayCircle } from 'lucide-react';
import WhatsAppTutorial from '@/components/whatsapp/WhatsAppTutorial';
import TutorialVideoModal from '@/components/common/TutorialVideoModal';
import { getTutorial } from '@/config/pageTutorials';
import {
  isAlertMetaHealth,
  metaHealthAlertCopy,
  metaHealthBadgeClass,
  metaHealthBadgeLabel,
  notifyWhatsAppAccountsUpdated,
  openMetaBusinessSupport,
} from '@/lib/metaAccountHealth';

interface ConnectedWhatsApp {
  id: string;
  phone_number: string;
  display_name: string;
  provider: 'meta' | 'gupshup';
  business_id: string;
  gupshup_app_id?: string;
  business_account_name?: string;
  business_timezone?: string;
  business_namespace?: string;
  account_review_status?: string;
  connected_at: string;
  status: 'active' | 'inactive' | 'pending_phone' | 'pending_embed';
  is_coexistence?: boolean | null;
  mm_lite_status?: string | null;
  messaging_limit?: string | null;
  waba_mode?: 'coexistence' | 'mm_lite' | 'standard' | string | null;
  webhook_format?: 'passthrough_v3' | 'classic_v2' | string | null;
  meta_health_status?: 'available' | 'limited' | 'restricted' | 'blocked' | null;
  meta_health_event?: string | null;
  meta_health_detail?: string | null;
  quality_rating?: string | null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const WhatsAppIntegration: React.FC = () => {
  const { user, token } = useAuth();

  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedWhatsApp[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [onboarding, setOnboarding] = useState<boolean>(false);
  const [pendingAppId, setPendingAppId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [pollTimer, setPollTimer] = useState<number>(60);
  const [showMetaAiTutorial, setShowMetaAiTutorial] = useState(false);
  const metaAiTutorial = getTutorial('whatsapp_meta_ai');

  // Poll for connection completion
  useEffect(() => {
    let interval: NodeJS.Timeout;
    let timerInterval: NodeJS.Timeout;

    if (isPolling && pendingAppId) {
      // Start countdown timer
      timerInterval = setInterval(() => {
        setPollTimer((prev) => {
          if (prev <= 1) return 0;
          return prev - 1;
        });
      }, 1000);

      // Poll every 5 seconds ONLY if timer is 0
      interval = setInterval(async () => {
        if (pollTimer > 0) return; // Wait until timer finishes

        try {
          // Try to go live. If not ready, it will fail/throw.
          // We suppress error toast here to avoid spamming user
          await handleRetryGoLive(pendingAppId, true);
        } catch (e) {
          // Ignore errors during polling
          console.log("Polling... not ready yet");
        }
      }, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [isPolling, pendingAppId, pollTimer]);

  const [phoneInput, setPhoneInput] = useState<string>('');
  const [showPhoneForm, setShowPhoneForm] = useState<boolean>(false);

  useEffect(() => {
    if (user && token) {
      loadConnectedAccounts();
    }
  }, [user, token]);

  const loadConnectedAccounts = async (): Promise<void> => {
    if (!user || !token) {
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(`${BACKEND_URL}/api/whatsapp/connected-accounts`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const accounts = response.data.accounts || [];
        setConnectedAccounts(accounts);
      } else {
        setConnectedAccounts([]);
      }
    } catch (error: any) {
      console.error('Error loading connected WhatsApp accounts:', error);
      if (error.response?.status !== 404) {
        toast.error('Error al cargar las cuentas de WhatsApp conectadas');
      }
      setConnectedAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Gupshup Onboarding Flow ────────────────────────────
  const handleStartGupshupOnboarding = async (): Promise<void> => {
    // 1. Open a new TAB immediately (synchronous with user click) to avoid popup blockers
    const newTab = window.open('about:blank', '_blank');

    // 2. Check if the browser blocked the tab
    if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
      toast.error('El navegador bloqueó la pestaña emergente', {
        description: 'Por favor permite ventanas emergentes para este sitio en la configuración de tu navegador y vuelve a intentar.',
        duration: 10000,
        action: {
          label: 'Entendido',
          onClick: () => { }
        }
      });
      return;
    }

    // 3. Show loading state in the new tab while we call the backend
    newTab.document.write(`
        <html>
            <head><title>Conectando WhatsApp...</title></head>
            <body style="font-family: sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; background-color: #f0fdf4; margin: 0;">
                <div style="text-align: center;">
                    <h2 style="color: #166534; margin-bottom: 10px;">Conectando con WhatsApp...</h2>
                    <p style="color: #4b5563;">Por favor espera, estamos preparando todo.</p>
                </div>
            </body>
        </html>
    `);

    try {
      setOnboarding(true);
      setPollTimer(60); // Reset timer
      toast.loading('Generando enlace de conexión...', { id: 'gupshup-onboard' });

      // Call Gupshup onboard endpoint without phone number (backend will use user name)
      const response = await axios.post(
        `${BACKEND_URL}/api/gupshup/onboard`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const { app_id, embed_link } = response.data;
        setPendingAppId(app_id);
        setIsPolling(true); // Start polling phase
        setPollTimer(60);   // 60 seconds countdown

        toast.success('¡Listo! Continúa en la nueva pestaña', {
          id: 'gupshup-onboard',
          duration: 5000
        });

        // Redirect the already open tab to the Gupshup URL
        if (embed_link) {
          newTab.location.href = embed_link;
        } else {
          newTab.close();
          throw new Error('No se recibió el link de conexión');
        }

        setShowPhoneForm(false);
      } else {
        newTab.close(); // Close if logic failed
        throw new Error(response.data.error || 'Error al iniciar el onboarding');
      }
    } catch (error: any) {
      newTab.close(); // Close on error
      console.error('Gupshup onboarding error:', error);
      toast.error('Error al iniciar la conexión', {
        id: 'gupshup-onboard',
        description: error.response?.data?.detail || error.message || 'Inténtalo de nuevo'
      });
      setOnboarding(false);
    }
  };

  const handleRetryGoLive = async (appId: string, silent: boolean = false): Promise<void> => {
    try {
      if (!silent) {
        setLoading(true);
        toast.loading('Finalizando conexión...', { id: 'gupshup-golive' });
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/gupshup/app/${appId}/go-live`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Clean up polling state
        setIsPolling(false);
        setPendingAppId(null);
        setOnboarding(false);

        toast.success('¡WhatsApp conectado exitosamente!', {
          id: 'gupshup-golive',
          description: response.data.phone_number || 'Cuenta vinculada'
        });
        await loadConnectedAccounts();
        notifyWhatsAppAccountsUpdated();
      } else {
        throw new Error(response.data.error || 'Error al finalizar');
      }
    } catch (error: any) {
      console.error('Go-live error:', error);
      if (!silent) {
        toast.error('Error al finalizar la conexión', {
          id: 'gupshup-golive',
          description: error.response?.data?.detail || error.message || 'Asegúrate de haber completado el registro en la pestaña de Gupshup'
        });
      } else {
        // Rethrow to let poller know it failed
        throw error;
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleDisconnectAccount = async (accountId: string): Promise<void> => {
    if (!confirm('¿Estás seguro de que deseas desconectar esta cuenta de WhatsApp?')) {
      return;
    }

    try {
      setLoading(true);

      const response = await axios.delete(
        `${BACKEND_URL}/api/whatsapp/connected-accounts/${accountId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success('Cuenta desconectada correctamente');
        setConnectedAccounts([]);
        await loadConnectedAccounts();
        notifyWhatsAppAccountsUpdated();
      } else {
        throw new Error(response.data.message || 'Error al desconectar');
      }
    } catch (error: any) {
      console.error('Error disconnecting account:', error);
      toast.error('Error al desconectar la cuenta', {
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncAccount = async (appId: string): Promise<void> => {
    try {
      setLoading(true);
      toast.loading('Sincronizando cuenta...', { id: 'gupshup-sync' });

      const response = await axios.post(
        `${BACKEND_URL}/api/gupshup/app/${appId}/sync`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Cuenta sincronizada correctamente', {
          id: 'gupshup-sync',
          description: `Número: ${response.data.phone_number}`
        });
        await loadConnectedAccounts();
        notifyWhatsAppAccountsUpdated();
      } else {
        throw new Error(response.data.message || 'Error al sincronizar');
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      toast.error('Error al sincronizar la cuenta', {
        id: 'gupshup-sync',
        description: error.response?.data?.detail || error.message || 'Inténtalo de nuevo'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
              <Phone className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">WhatsApp</h1>
              <p className="text-gray-600 mt-1">Conecta tu número para enviar recordatorios</p>
            </div>
          </div>
          <div className="flex items-center gap-3 justify-center sm:justify-end">
            {!loading && connectedAccounts.length === 0 && !pendingAppId && (
              <Button
                id="whatsapp-connect-top-btn"
                onClick={handleStartGupshupOnboarding}
                disabled={loading || onboarding}
                className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-6 h-11 rounded-xl shadow-lg flex items-center gap-2 font-semibold transition-all active:scale-95"
              >
                {onboarding ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Plus className="h-5 w-5" />
                )}
                Conectar WhatsApp
              </Button>
            )}
          </div>
        </div>

        {/* Pending Onboarding: User started but hasn't completed Gupshup embed */}
        {pendingAppId && isPolling && (
          <Card className="border-2 border-blue-200 shadow-lg overflow-hidden animate-pulse-subtle">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-gray-900">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md animate-spin-slow">
                    <Loader2 className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <div className="text-xl">Completando conexión...</div>
                    <div className="text-sm font-normal text-gray-600 mt-1">
                      {pollTimer > 0
                        ? "Esperando a que completes el registro..."
                        : "Verificando estado de la conexión..."}
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
            </div>

            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="bg-white rounded-xl border-2 border-blue-100 p-6 flex flex-col items-center text-center">
                  <p className="text-gray-700 leading-relaxed max-w-lg mx-auto">
                    Por favor completa el proceso en la pestaña de Gupshup.
                    <br />
                    <strong>No cierres esta página.</strong> Detectaremos automáticamente cuando termines.
                  </p>
                  {pollTimer === 0 && (
                    <p className="text-sm text-blue-600 mt-4 animate-pulse font-medium">
                      Verificando conexión final...
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}



        {/* Main Card: shown when no accounts and not in a flow */}
        {!loading && connectedAccounts.length === 0 && !pendingAppId && (
          <Card className="border-2 border-green-100 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-gray-900">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                    <MessageCircle className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <div className="text-xl">Conecta tu WhatsApp</div>
                    <div className="text-sm font-normal text-gray-600 mt-1">
                      Vincula tu número de WhatsApp Business para empezar a enviar mensajes
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
            </div>

            <CardContent className="pt-6">
              <div className="space-y-6">
                <WhatsAppTutorial />
                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />

                    <div>
                      <p className="text-sm font-semibold text-amber-900">
                        ¿Tu cuenta no cumple con los requisitos para conectarse?
                      </p>

                      <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                        Si tu cuenta tiene Meta IA activado u otra restricción, revisa este video antes de continuar con la conexión.
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowMetaAiTutorial(true)}
                    className="border-amber-300 text-amber-800 hover:bg-amber-100 hover:text-amber-900 shrink-0"
                  >
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Ver video
                  </Button>
                </div>
              </div>

                <div className="bg-white rounded-xl border-2 border-green-100 p-6">
                  <div className="prose prose-sm max-w-none">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Aquí podrás conectar un número de WhatsApp para enviar recordatorios desde la app.
                      Al vincularlo, los mensajes saldrán desde ese número, para que tus pacientes/usuarios
                      reconozcan quién les escribe.
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
                  <Button
                    id="whatsapp-connect-btn"
                    onClick={handleStartGupshupOnboarding}
                    disabled={loading || onboarding}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 rounded-xl"
                  >
                    {onboarding ? (
                      <><Loader2 className="h-6 w-6 mr-2 animate-spin" /> Conectando...</>
                    ) : (
                      <><Plus className="h-6 w-6 mr-2" /> Conectar WhatsApp</>
                    )}
                  </Button>
                </div>

                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-5 flex items-start gap-4">
                  <AlertCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-blue-900 font-semibold mb-1">Información importante</p>
                    <p className="text-blue-800 text-sm leading-relaxed">
                      Necesitas un número que no esté siendo utilizado por WhatsApp o WhatsApp Business, podrías.
                      <br />
                      Eliminar la cuenta de WhatsApp del número que deseas utilizar.
                      <br />
                      Conectar un número nuevo. (Nuevo chip o SimCard)
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading Skeleton Indicator */}
        {loading && connectedAccounts.length === 0 && !pendingAppId && (
          <div className="flex items-center justify-center p-12 bg-white rounded-xl border border-gray-100 shadow-sm">
            <Loader2 className="h-8 w-8 text-emerald-500 animate-spin" />
            <span className="ml-3 text-gray-500 font-medium">Verificando cuentas de WhatsApp...</span>
          </div>
        )}

        {/* Connected Accounts Section */}
        {connectedAccounts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Cuentas Conectadas
              </CardTitle>
              <CardDescription>
                Números de WhatsApp vinculados a tu cuenta
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {connectedAccounts.map((account) => (
                  <div
                    key={account.id}
                    className={`${
                      account.phone_number?.trim().toLowerCase() === 'pending'
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-100'
                    } rounded-lg border-2 hover:shadow-md transition-shadow overflow-hidden`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 p-4">
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-12 h-12 ${
                            account.phone_number?.trim().toLowerCase() === 'pending'
                              ? 'bg-yellow-500'
                              : 'bg-gradient-to-r from-green-500 to-emerald-600'
                          } rounded-full flex items-center justify-center flex-shrink-0`}
                        >
                          <MessageCircle className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-gray-900">{account.display_name}</h4>
                            {(account.waba_mode === 'coexistence' || account.is_coexistence === true) && (
                              <Badge
                                variant="outline"
                                className="text-xs bg-purple-50 text-purple-800 border-purple-200"
                              >
                                Coexistencia
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{account.phone_number}</p>
                          <Badge
                            variant={
                              account.phone_number?.trim().toLowerCase() === 'pending'
                                ? 'secondary'
                                : account.status === 'active' && !isAlertMetaHealth(account.meta_health_status)
                                  ? 'default'
                                  : 'secondary'
                            }
                            className={
                              account.phone_number?.trim().toLowerCase() === 'pending'
                                ? 'bg-yellow-100 text-yellow-700 border border-yellow-200 hover:bg-yellow-100 mt-1'
                                : `${metaHealthBadgeClass(account.meta_health_status, account.status)} mt-1`
                            }
                          >
                            {account.phone_number?.trim().toLowerCase() === 'pending'
                              ? 'Pendiente'
                              : metaHealthBadgeLabel(account.meta_health_status, account.status)}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full">
                        {account.provider === 'gupshup' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSyncAccount(account.gupshup_app_id || account.business_id)}
                            disabled={loading}
                            className="w-full sm:w-auto text-blue-600 hover:bg-blue-50 hover:text-blue-700 border-blue-200"
                          >
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Sincronizar
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDisconnectAccount(account.id)}
                          className="w-full sm:w-auto text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                        >
                          Desconectar
                        </Button>
                      </div>
                    </div>

                    {/* Meta restricted / blocked warning (limited does not alert) */}
                    {account.status === 'active' && isAlertMetaHealth(account.meta_health_status) && (() => {
                      const copy = metaHealthAlertCopy(account.meta_health_status);
                      return (
                      <div className="border-t border-red-200 bg-red-50 p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-red-800">
                                {copy.title}
                              </p>
                              <p className="text-sm text-red-700 mt-0.5">
                                {copy.body}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={openMetaBusinessSupport}
                            className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white hover:opacity-90 shadow-sm shrink-0 h-9 px-4 rounded-lg font-semibold"
                          >
                            Revisar en Meta Business
                            <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                          </Button>
                        </div>
                      </div>
                      );
                    })()}

                    {/* Warning for pending phone verification - only shown when NOT active */}
                    {account.status !== 'active' && (
                      <div className="border-t border-yellow-200 bg-yellow-50 p-4">
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-yellow-900 mb-1">
                                Acción requerida: {account.status === 'pending_embed' ? 'Completar registro' : 'Verificar número de teléfono'}
                              </p>
                              <p className="text-sm text-yellow-800 leading-relaxed">
                                {account.status === 'pending_embed'
                                  ? 'Debes finalizar la configuración para activar el envío de mensajes.'
                                  : 'Tu cuenta está conectada, pero necesitas verificar tu número de teléfono.'
                                }
                              </p>
                            </div>
                          </div>

                          {account.status === 'pending_embed' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                const appId = account.gupshup_app_id || account.business_id;
                                if (appId) {
                                  handleRetryGoLive(appId);
                                } else {
                                  toast.error("No se encontró el ID de la aplicación");
                                  console.error("Missing app ID for account:", account);
                                }
                              }}
                              disabled={loading}
                              className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border border-yellow-300 shadow-sm shrink-0"
                            >
                              Completar ahora
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {metaAiTutorial && (
          <TutorialVideoModal
            open={showMetaAiTutorial}
            onOpenChange={setShowMetaAiTutorial}
            youtubeId={metaAiTutorial.youtubeId}
            title={metaAiTutorial.title}
          />
        )}
    </div>
  </div>
  );
};

export default WhatsAppIntegration;
